import { useEffect, useMemo, useState } from "react";
import {
  Ban,
  BookOpen,
  Building2,
  CircleAlert,
  KeyRound,
  LifeBuoy,
  Lightbulb,
  MapPin,
  Puzzle,
  RotateCw,
  Rocket,
  ShieldCheck,
} from "lucide-react";
import useInstall from "../../hook/useInstall";
import { useAppServices } from "../../hook/services";
import { cx } from "../../utils";
import AppIcon from "../../components/ui/AppIcon";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import EmptyState from "../../components/ui/EmptyState";
import Skeleton from "../../components/ui/Skeleton";
import GetStartedPanel from "./GetStartedPanel";
import CredentialsPanel from "./CredentialsPanel";
import UsagePanel from "./UsagePanel";
import FeatureRequestsPanel from "./FeatureRequestsPanel";
import SupportPanel from "./SupportPanel";
import AnnouncementBar from "../../components/AnnouncementBar";
import NotificationBell from "../../components/NotificationBell";

const TABS = [
  { id: "get-started", label: "Get started", icon: Rocket },
  { id: "credentials", label: "Credentials", icon: KeyRound },
  { id: "usage", label: "How to use", icon: BookOpen },
  { id: "feature-requests", label: "Feature requests", icon: Lightbulb },
  { id: "support", label: "Support", icon: LifeBuoy },
];

// Setup progress as a ring: the share of steps done, with the count inside.
// Only a conversation provider has an inbound webhook to register.
const isConversationProvider = (app) =>
  (app?.featuresInclude || []).some(
    (f) =>
      String(typeof f === "string" ? f : f?.name || f?.label || "").toLowerCase() ===
      "conversation provider",
  );

/**
 * The open tab survives a reload. It's kept in two places, because this page
 * is reloaded two ways:
 *   - in the address (#credentials) — a reload of the page itself, or a link
 *     straight to a tab;
 *   - in browser storage, per app — a full reload of the CRM loads this page
 *     fresh from the marketplace URL, with no #tab on it.
 * Storage can throw (private mode, blocked site data, sandboxed frames); the
 * page then simply opens on the first tab.
 */
const TAB_IDS = TABS.map((t) => t.id);
const storageKey = (appId) => `app-tab:${appId || "unknown"}`;

const readSavedTab = (appId) => {
  const fromHash = window.location.hash.replace(/^#/, "");
  if (TAB_IDS.includes(fromHash)) return fromHash;
  try {
    const stored = localStorage.getItem(storageKey(appId));
    if (TAB_IDS.includes(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  return "get-started";
};

const saveTab = (appId, id) => {
  try {
    localStorage.setItem(storageKey(appId), id);
  } catch {
    /* storage unavailable */
  }
  // replaceState, so tabs don't pile up in the back button's history.
  try {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${id}`);
  } catch {
    /* sandboxed frame */
  }
};

// A field is required unless its help text opens with "Optional".
const isRequired = (field) => !/^optional\b/i.test(field.short_description || "");

/**
 * The app's custom page inside your CRM.
 *
 * Rendering is gated twice: SSO has to tell us which account is looking, then
 * that account has to actually have the app installed. Until both hold, the
 * page shows why rather than an empty shell.
 */
const AppPage = () => {
  const {
    loading,
    installed,
    app,
    install,
    error,
    appId,
    locationId,
    companyId,
    user,
    contextSource,
  } = useInstall();

  const [tab, setTabState] = useState(() => readSavedTab(appId));
  const setTab = (id) => {
    setTabState(id);
    saveTab(appId, id);
  };

  // appId can arrive after the first render; pick up that app's saved tab then.
  useEffect(() => {
    if (appId) setTabState(readSavedTab(appId));
  }, [appId]);

  // A link that only changes the #tab doesn't reload the page — follow it.
  useEffect(() => {
    const onHash = () => {
      const id = window.location.hash.replace(/^#/, "");
      if (TAB_IDS.includes(id)) {
        setTabState(id);
        saveTab(appId, id);
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [appId]);
  const [credentials, setCredentials] = useState(null);
  // The webhook tick is saved from the Get started tab; mirrored here so the
  // How to use tab knows about it without waiting for the install to reload.
  const [webhookConfirmed, setWebhookConfirmed] = useState(null);
  // Unread support replies, for the badge on the Support tab. Refreshed when
  // leaving the tab, since reading them there clears the count.
  const [supportUnread, setSupportUnread] = useState(0);

  // Load saved values once so the header and checklist can show real progress
  // instead of assuming the app is unconfigured.
  const { installs, support } = useAppServices();

  useEffect(() => {
    if (!installed || !appId || tab === "support") return undefined;
    let alive = true;
    const query = new URLSearchParams({
      appId,
      ...(locationId && { locationId }),
      ...(companyId && { companyId }),
    }).toString();
    support.List(query).then(({ response }) => {
      if (alive && Array.isArray(response)) setSupportUnread(response.reduce((n, t) => n + (t.unread || 0), 0));
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installed, appId, locationId, companyId, tab]);
  useEffect(() => {
    if (!installed || !appId) return undefined;
    let alive = true;

    (async () => {
      const query = new URLSearchParams({
        appId,
        ...(locationId && { locationId }),
        ...(companyId && { companyId }),
      }).toString();
      const { response } = await installs.GetCredentials(query);
      if (alive) setCredentials(response?.credentials || {});
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installed, appId, locationId, companyId]);

  const progress = useMemo(() => {
    const required = (app?.formFields || []).filter(isRequired);
    if (required.length === 0) return { total: 0, done: 0, ready: true };
    if (!credentials) return { total: required.length, done: 0, ready: false };
    const done = required.filter(
      (f) => String(credentials[f.key] || "").trim().length > 0,
    ).length;
    return { total: required.length, done, ready: done === required.length };
  }, [app, credentials]);

  /* ---------------- loading ---------------- */

  if (loading) {
    return (
      <div className="anim-in">
        <div className="flex items-center gap-4">
          <Skeleton className="h-16 w-16" rounded="rounded-2xl" />
          <div className="flex-1 space-y-2.5">
            <Skeleton className="h-6 w-52" />
            <Skeleton className="h-3.5 w-72" />
          </div>
        </div>
        <Skeleton className="mt-7 h-9 w-64" rounded="rounded-lg" />
        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <Skeleton className="aspect-video w-full" rounded="rounded-2xl" />
          <Skeleton className="h-64 w-full" rounded="rounded-2xl" />
        </div>
      </div>
    );
  }

  /* ---------------- blocked ---------------- */

  if (error) {
    return (
      <Card padded={false} className="anim-in">
        <EmptyState
          tone="rose"
          icon={<CircleAlert size={24} />}
          title="Can't open this app"
          description={error}
          action={
            <Button variant="secondary" size="sm" icon={<RotateCw size={14} />} onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        />
      </Card>
    );
  }

  if (!app) {
    return (
      <Card padded={false} className="anim-in">
        <EmptyState
          tone="rose"
          icon={<Puzzle size={24} />}
          title="App not found"
          description="No app matches that ID. Check the custom page URL configured in the marketplace listing."
        />
      </Card>
    );
  }

  if (!installed) {
    return (
      <Card padded={false} className="anim-in">
        <EmptyState
          tone="amber"
          icon={<Ban size={24} />}
          title={`${app.appName} isn't installed here`}
          description={
            install
              ? "This app was uninstalled from this account. Reinstall it from the marketplace to use it again."
              : "This account doesn't have the app installed. Install it from the marketplace to get access."
          }
        />
      </Card>
    );
  }

  /* ---------------- ready ---------------- */

  const accountName = install?.locationName || install?.companyName;

  // An app with no usage guide written yet doesn't get an empty tab.
  const tabs = TABS.filter((t) => t.id !== "usage" || app.usage?.sections?.length > 0);
  // A saved tab this app doesn't show (no usage guide, say) falls back to the
  // first one rather than to whatever the panel chain ends on.
  const current = tabs.some((t) => t.id === tab) ? tab : "get-started";

  const needsWebhook = isConversationProvider(app);
  const webhookDone =
    Boolean(install?.webhookVerifiedAt) ||
    (webhookConfirmed ?? Boolean(install?.webhookConfirmedAt));
  const setupDone = progress.ready && (!needsWebhook || webhookDone);

  // The setup steps the ring counts: the same ones the Get started stepper shows.
  const steps = [
    { id: "installed", done: true },
    { id: "credentials", done: progress.ready },
    ...(needsWebhook ? [{ id: "webhook", done: webhookDone }] : []),
  ];
  const stepsDone = steps.filter((x) => x.done).length;


  return (
    <div className="anim-in">
      {/* Announcements from the team — renders nothing when there are none. */}
      <AnnouncementBar appId={appId} className="mb-5" />

      {/* Identity and tabs in one card. No overflow-hidden here: the bell's
          panel opens out of this card and would be cut off — only the gradient
          layer is clipped to the corners. */}
      <header className="relative z-30 rounded-2xl border border-ink-200/70 bg-white shadow-card">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-full overflow-hidden rounded-2xl" aria-hidden>
          <div className="absolute inset-0 bg-mesh-1" />
        </div>

        <div className="relative flex flex-wrap items-center gap-4 px-5 pb-4 pt-5 sm:px-6">
          <span className="rounded-xl bg-white p-0.5 shadow-card-elevated ring-1 ring-ink-200/60">
            <AppIcon app={app} size="lg" />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-[1.35rem] font-bold leading-tight tracking-tight text-ink-900">{app.appName}</h1>
              {setupDone ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[12px] font-semibold text-emerald-700 ring-1 ring-emerald-200/80">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping-slow rounded-full bg-emerald-400" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                  Live
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[12px] font-semibold text-amber-700 ring-1 ring-amber-200/80">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  Setup needed
                </span>
              )}
            </div>

            {/* Where this is running and who's looking, then what the app is. */}
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {accountName && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-2.5 py-0.5 text-[12px] font-medium text-ink-700 ring-1 ring-ink-200/80">
                  {locationId ? <MapPin size={12} className="text-ink-400" /> : <Building2 size={12} className="text-ink-400" />}
                  {accountName}
                </span>
              )}
              {user?.userName && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-2.5 py-0.5 text-[12px] font-medium text-ink-700 ring-1 ring-ink-200/80">
                  <ShieldCheck size={12} className="text-emerald-500" />
                  {user.userName}
                  {user.role && <span className="font-normal capitalize text-ink-400">· {user.role}</span>}
                </span>
              )}
              {app.shortDescription && (
                <span className="hidden min-w-0 truncate text-[12.5px] text-ink-500 md:inline">{app.shortDescription}</span>
              )}
            </div>
          </div>

          {/* Messages from the team to this account. */}
          <NotificationBell appId={appId} locationId={locationId} companyId={companyId} />
        </div>

        {/* Tabs, along the bottom edge of the header. */}
        <div className="relative flex items-center gap-3 border-t border-ink-150 px-2 sm:px-4">
          <nav className="-mb-px flex min-w-0 flex-1 overflow-x-auto" aria-label="Sections">
            {tabs.map(({ id, label, icon: Icon }) => {
              const isActive = current === id;
              const flag = id === "credentials" && !progress.ready && progress.total > 0;
              const count = id === "support" ? supportUnread : 0;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  aria-current={isActive ? "page" : undefined}
                  className={cx(
                    "relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3.5 py-3 text-[13.5px] font-medium transition-colors",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500",
                    isActive ? "border-brand-600 text-brand-700" : "border-transparent text-ink-500 hover:border-ink-200 hover:text-ink-900",
                  )}
                >
                  <Icon size={15} className={isActive ? "text-brand-600" : "text-ink-400"} />
                  {label}
                  {flag && <span className="h-2 w-2 rounded-full bg-amber-500" aria-label="Needs attention" />}
                  {count > 0 && (
                    <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-brand-600 px-1.5 text-[10.5px] font-bold text-white" aria-label={`${count} unread`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Setup at a glance; a click goes where it's useful next. */}
          <button
            type="button"
            onClick={() => setTab(setupDone && tabs.some((t) => t.id === "usage") ? "usage" : "get-started")}
            className="hidden shrink-0 items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-ink-50 lg:flex"
            title={setupDone ? "See how to use it" : "Finish setup"}
          >
            <span className="text-[12px] text-ink-500">Setup</span>
            <span className="flex gap-1" aria-hidden>
              {steps.map((st) => (
                <span key={st.id} className={cx("h-1.5 w-7 rounded-full", st.done ? "bg-emerald-500" : "bg-ink-200")} />
              ))}
            </span>
            <span className="text-[12px] font-semibold tabular-nums text-ink-800">
              {setupDone ? "Done" : `${stepsDone} of ${steps.length}`}
            </span>
          </button>
        </div>
      </header>

      {contextSource === "url" && (
        <p className="mt-3 rounded-lg border border-dashed border-ink-200 px-3.5 py-2 text-[11.5px] text-ink-400">
          Local development: account read from the URL because the CRM sign-in didn't respond. In production this page is framed by the CRM.
        </p>
      )}

      <div className="mt-5 animate-fade-in" key={current}>
        {current === "get-started" ? (
          <GetStartedPanel
            app={app}
            install={install}
            progress={progress}
            onOpenCredentials={() => setTab("credentials")}
            onOpenUsage={tabs.some((t) => t.id === "usage") ? () => setTab("usage") : undefined}
            onOpenSupport={() => setTab("support")}
            appId={appId}
            locationId={locationId}
            companyId={companyId}
            onWebhookConfirmed={setWebhookConfirmed}
          />
        ) : current === "usage" ? (
          <UsagePanel app={app} ready={setupDone} locationId={locationId} onOpenSetup={() => setTab("get-started")} />
        ) : current === "feature-requests" ? (
          <FeatureRequestsPanel app={app} appId={appId} locationId={locationId} companyId={companyId} user={user} />
        ) : current === "support" ? (
          <SupportPanel app={app} appId={appId} locationId={locationId} companyId={companyId} user={user} />
        ) : (
          <CredentialsPanel
            app={app}
            appId={appId}
            locationId={locationId}
            companyId={companyId}
            onSaved={setCredentials}
            onOpenGuide={() => setTab("get-started")}
            onOpenSupport={() => setTab("support")}
          />
        )}
      </div>
    </div>
  );
};

export default AppPage;
